import React, { useMemo, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { chartColors } from '../../themes/theme'
import { useTheme } from '@material-ui/styles';
import { Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles'
import { Player } from '../../models/ServerModels';
import { allValuesAreTheSame, isStringNumber, limitDecimalPlaces, median, randomItem } from '../../util/util';

const Chart = require('react-chartjs-2').Chart

interface IResultsChartProps {
    players: Player[]
    animate?: boolean
}

const CHART_PX = 160

const useStyles = makeStyles(() => ({
    wrap: {
        width: CHART_PX,
        margin: '0 auto',
        flexShrink: 0,
        boxSizing: 'border-box',
    },
    canvas: {
        width: CHART_PX,
        height: CHART_PX,
        maxWidth: CHART_PX,
        maxHeight: CHART_PX,
        position: 'relative',
        overflow: 'hidden',
        '& canvas': {
            width: `${CHART_PX}px !important`,
            height: `${CHART_PX}px !important`,
        },
    },
    caption: {
        marginTop: 6,
        padding: 0,
        lineHeight: 1.2,
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
    },
}))

function convertData(list: string[]): Map<string, number> {
    return new Map(list.map(
        x => [x, list.filter(y => y === x).length]
    ))
}

export default function ResultsChart({ players, animate = true }: IResultsChartProps) {
    const classes = useStyles()
    const theme = useTheme<Theme>()
    Chart.defaults.global.defaultFontColor = theme.palette.text.primary

    const allEstimates = players.map(p => p.estimate.values).flat().filter(e => e.length > 0).map(p => p === "½" ? "0.5" : p)
    const estimateNumbers = allEstimates.filter(e => isStringNumber(e)).map(e => parseFloat(e))
    const isUnanimous = allEstimates.length > 0 && allValuesAreTheSame(allEstimates)
    const meanEstimate = estimateNumbers.length > 0 ? limitDecimalPlaces((estimateNumbers.reduce((a, b) => a + b) / estimateNumbers.length), 2) : 0
    const medianEstimate = estimateNumbers.length > 0 ? limitDecimalPlaces(median(estimateNumbers), 2) : 0
    const estimatesKey = allEstimates.join('\0')

    const unanimousWords = ["Unanimous", "Undisputed", "Like-Minded", "Agreement"]
    const [unanimousWord] = useState(randomItem(unanimousWords))
    const unanimousEmojis = ["👍", "💯", "👏", "😎", "😏", "🔥", "👀", "🎉", "🎂", "🐱‍👤", "🌟", "🧠", "🕺", "🎯"]
    const [unanimousEmoji] = useState(randomItem(unanimousEmojis))

    const barData = useMemo(() => {
        const list = convertData(allEstimates)
        return {
            datasets: [{
                data: Array.from(list.values()),
                borderWidth: 0,
                backgroundColor: chartColors
            }],
            labels: Array.from(list.keys())
        }
    }, [estimatesKey])

    return (
        <div className={classes.wrap}>
            <div className={classes.canvas}>
                <Doughnut
                    data={barData}
                    height={CHART_PX}
                    width={CHART_PX}
                    options={{
                        responsive: false,
                        maintainAspectRatio: false,
                        animation: { duration: animate ? 400 : 0 },
                        tooltips: { enabled: false },
                        hover: { mode: null },
                        cutoutPercentage: 70,
                        legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                                boxWidth: 10,
                                fontSize: 11,
                                padding: 8,
                            },
                        }
                    }} />
            </div>

            {isUnanimous ?
                <Typography variant="h6" className={classes.caption}>{unanimousWord}! {unanimousEmoji}</Typography>
                : meanEstimate > 0 ? <Typography className={classes.caption}>Mean: {meanEstimate} | Median: {medianEstimate}</Typography> : <></>
            }

        </div>
    )
}
